/**
 * Backend Keep-Alive Service
 * Prevents Cloud Run cold starts by pinging the backend periodically
 */

interface KeepAliveConfig {
  backendUrl?: string;
  pingInterval?: number; // in milliseconds
  enableLogging?: boolean;
  businessHoursOnly?: boolean;
}

interface HealthResponse {
  status: string;
  timestamp: string;
  service: string;
}

interface WarmupResponse {
  status: string;
  timestamp: string;
  services: Record<string, boolean>;
  message: string;
}

class BackendKeepAlive {
  private backendUrl: string;
  private pingInterval: number;
  private enableLogging: boolean;
  private businessHoursOnly: boolean;
  private intervalId: NodeJS.Timeout | null = null;
  private isActive: boolean = false;
  private lastPingTime: Date | null = null;

  constructor(config: KeepAliveConfig = {}) {
    this.backendUrl = config.backendUrl || process.env.REACT_APP_API_URL || '/api/v1';
    this.pingInterval = config.pingInterval || 10 * 60 * 1000; // 10 minutes default
    this.enableLogging = config.enableLogging ?? true;
    this.businessHoursOnly = config.businessHoursOnly ?? false;
  }

  /**
   * Start the keep-alive service
   */
  start(): void {
    if (this.isActive) {
      this.log('Keep-alive service already running');
      return;
    }

    this.log('🔥 Starting backend keep-alive service');
    this.isActive = true;

    // Ping immediately
    this.ping();

    // Set up interval pings
    this.intervalId = setInterval(() => {
      if (this.shouldPing()) {
        this.ping();
      }
    }, this.pingInterval);
  }

  /**
   * Stop the keep-alive service
   */
  stop(): void {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
    this.isActive = false;
    this.log('❄️ Stopped backend keep-alive service');
  }

  /**
   * Check if we should ping based on business hours setting
   */
  private shouldPing(): boolean {
    if (!this.businessHoursOnly) {
      return true;
    }

    const now = new Date();
    const hour = now.getHours();
    
    // Only ping during business hours (8 AM to 10 PM)
    return hour >= 8 && hour <= 22;
  }

  /**
   * Send a health check ping to the backend
   */
  private async ping(): Promise<void> {
    try {
      const response = await fetch(`${this.backendUrl}/health`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        // Don't include auth headers for health checks
      });

      if (response.ok) {
        const data: HealthResponse = await response.json();
        this.lastPingTime = new Date();
        this.log(`💓 Backend keep-alive ping successful: ${data.timestamp}`);
      } else {
        this.log(`⚠️ Backend keep-alive ping failed: ${response.status}`, 'warn');
      }
    } catch (error) {
      this.log(`⚠️ Backend keep-alive ping error: ${error instanceof Error ? error.message : 'Unknown error'}`, 'warn');
    }
  }

  /**
   * Warmup the backend (call this on user interaction)
   */
  async warmup(): Promise<WarmupResponse | null> {
    try {
      this.log('🔥 Warming up backend...');
      const response = await fetch(`${this.backendUrl}/warmup`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data: WarmupResponse = await response.json();
        this.log(`✅ Backend warmup complete: ${data.message}`);
        return data;
      } else {
        this.log(`⚠️ Backend warmup failed: ${response.status}`, 'warn');
        return null;
      }
    } catch (error) {
      this.log(`⚠️ Backend warmup error: ${error instanceof Error ? error.message : 'Unknown error'}`, 'warn');
      return null;
    }
  }

  /**
   * Get the status of the keep-alive service
   */
  getStatus() {
    return {
      isActive: this.isActive,
      lastPingTime: this.lastPingTime,
      pingInterval: this.pingInterval,
      businessHoursOnly: this.businessHoursOnly,
    };
  }

  /**
   * Update configuration
   */
  updateConfig(config: Partial<KeepAliveConfig>): void {
    if (config.pingInterval) {
      this.pingInterval = config.pingInterval;
      // Restart if active to apply new interval
      if (this.isActive) {
        this.stop();
        this.start();
      }
    }
    if (config.enableLogging !== undefined) {
      this.enableLogging = config.enableLogging;
    }
    if (config.businessHoursOnly !== undefined) {
      this.businessHoursOnly = config.businessHoursOnly;
    }
  }

  /**
   * Log messages (can be disabled)
   */
  private log(message: string, _level: 'log' | 'warn' | 'error' = 'log'): void {
    // if (this.enableLogging) {
    //   console[level](`[KeepAlive] ${message}`);
    // }
  }
}

// Create and export a singleton instance
export const keepAliveService = new BackendKeepAlive({
  enableLogging: process.env.NODE_ENV === 'development',
  businessHoursOnly: false, // Set to true if you want to save even more on pings
});

export default BackendKeepAlive; 